import { useState } from 'react'
import {
  useCertificateSuggestions,
  useCertificateSummary,
  useCertificates,
  useDeleteCertificate,
  useSaveCertificate,
  useUsers
} from '../api/queries'
import type { Certificate, CertificateInput } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { Avatar, Card, EmptyState, ErrorBox, Loading } from '../components/ui'

function formatDate(value?: string | null) {
  if (!value) return '-'
  const [y, m, d] = value.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * "Udløb for 12 dage siden", "Udløber i dag", "Udløber om 3 måneder".
 *
 * Enheden vokser med afstanden: "om 187 dage" siger ikke noget om, hvor meget
 * det haster, og "om 36 måneder" er ikke til at regne om i hovedet. Den præcise
 * dato står lige nedenunder for den, der skal bruge den.
 */
function describeExpiry(days?: number | null) {
  if (days === null || days === undefined) return 'Udløber ikke'
  if (days < 0) {
    const n = Math.abs(days)
    return n === 1 ? 'Udløb i går' : `Udløb for ${n} dage siden`
  }
  if (days === 0) return 'Udløber i dag'
  if (days === 1) return 'Udløber i morgen'
  if (days < 60) return `Udløber om ${days} dage`
  if (days < 365) return `Udløber om ${Math.round(days / 30)} måneder`

  const years = Math.round((days / 365) * 10) / 10
  return `Udløber om ${years.toString().replace('.', ',')} år`
}

/** Farven på stregen ude i venstre side: rød når det er udløbet, gul når det nærmer sig. */
function colorFor(certificate: Certificate, warningDays: number) {
  if (certificate.isExpired) return '#DC2626'
  const days = certificate.daysUntilExpiry
  if (days !== null && days !== undefined && days <= warningDays) return '#D97706'
  return 'transparent'
}

/**
 * Certifikater og kurser (beslutning 054).
 *
 * Alle må se listen - skal der findes en mand med kølecertifikat til i morgen,
 * skal man kunne slå det op. Kun administratorer kan oprette og rette: et bevis
 * er noget, en anden har udstedt.
 *
 * Systemet spærrer ikke for noget. Et udløbet bevis forhindrer ikke, at manden
 * bliver planlagt - det er en oplysning til dem, der fordeler arbejdet.
 */
export function CertificatesPage() {
  const { isAdmin } = useAuth()
  const [search, setSearch] = useState('')
  const [dueOnly, setDueOnly] = useState(false)
  const [includeInactive, setIncludeInactive] = useState(false)
  const [editing, setEditing] = useState<Certificate | null | undefined>(undefined)

  const { data, isLoading, error } = useCertificates({ search, dueOnly, includeInactive })
  const { data: summary } = useCertificateSummary()
  const remove = useDeleteCertificate()

  const warningDays = summary?.warningDays ?? 60

  async function handleDelete(certificate: Certificate) {
    const ok = confirm(
      `Slet "${certificate.name}" på ${certificate.user.displayName}?\n\n` +
        'Skal historikken bevares - ved et tilsyn spørges der om, hvad manden havde dengang - ' +
        'så luk beviset i stedet ved at fjerne fluebenet "Gyldigt bevis".'
    )
    if (!ok) return
    await remove.mutateAsync(certificate.id)
  }

  return (
    <>
      <div className="cal-toolbar">
        <div className="cal-title">
          {data?.length ?? 0} {(data?.length ?? 0) === 1 ? 'bevis' : 'beviser'}
        </div>

        <input
          placeholder="Søg i navn, udsteder eller medarbejder ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 280 }}
        />

        <label className="cal-person" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={dueOnly} onChange={(e) => setDueOnly(e.target.checked)} />
          Kun dem der haster
        </label>

        <label className="cal-person" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Vis udgåede
        </label>

        {isAdmin && (
          <button className="btn btn--primary btn--sm" onClick={() => setEditing(null)}>
            Registrér bevis
          </button>
        )}
      </div>

      {summary && (summary.expired > 0 || summary.dueSoon > 0) && (
        <div
          className="banner"
          style={{
            marginBottom: 12,
            borderLeft: `4px solid ${summary.expired > 0 ? '#DC2626' : '#D97706'}`
          }}
        >
          {summary.expired > 0 && (
            <>
              <strong>
                {summary.expired} {summary.expired === 1 ? 'bevis er udløbet' : 'beviser er udløbet'}.
              </strong>{' '}
              {/* Konsekvensen hoerer kun til de udloebne. Et bevis, der udloeber
                  om to maaneder, daekker jo stadig arbejdet i dag. */}
              Systemet spærrer ikke for noget - men den, det handler om, må ikke udføre det
              arbejde, beviset dækker.{' '}
            </>
          )}
          {summary.dueSoon > 0 && (
            <>
              {summary.dueSoon} {summary.dueSoon === 1 ? 'bevis udløber' : 'beviser udløber'} inden
              for {summary.warningDays} dage - book kurset i god tid.
            </>
          )}
        </div>
      )}

      {error && <ErrorBox error={error} />}
      {isLoading && <Loading />}

      {data && data.length === 0 && (
        <EmptyState>
          {search || dueOnly
            ? 'Ingen beviser passer på det, du søger efter.'
            : isAdmin
              ? 'Der står ingen beviser endnu. Tryk Registrér bevis for at skrive L-AUS, førstehjælp eller kølecertifikat ind - så siger systemet selv til, før de udløber.'
              : 'Der står ingen beviser endnu. En administrator kan skrive dem ind.'}
        </EmptyState>
      )}

      {data && data.length > 0 && (
        <Card>
          <div className="stack" style={{ gap: 8 }}>
            {data.map((c) => (
              <div
                key={c.id}
                className="row"
                style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap', opacity: c.isActive ? 1 : 0.55 }}
              >
                <span
                  style={{
                    width: 6,
                    alignSelf: 'stretch',
                    minHeight: 40,
                    borderRadius: 3,
                    background: colorFor(c, warningDays),
                    flexShrink: 0
                  }}
                />
                <Avatar user={c.user} size={30} />

                <div style={{ minWidth: 180, flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>
                    {c.name}
                    {!c.isActive && <span className="tiny muted"> · udgået</span>}
                  </div>
                  <div className="small muted">
                    {c.user.displayName}
                    {c.issuer && ` · ${c.issuer}`}
                    {c.certificateNumber && ` · nr. ${c.certificateNumber}`}
                  </div>
                  {c.notes && <div className="small">{c.notes}</div>}
                </div>

                <div style={{ minWidth: 170 }}>
                  <div
                    className="small"
                    style={{
                      fontWeight: c.isExpired ? 600 : 400,
                      color: c.isExpired ? '#DC2626' : undefined
                    }}
                  >
                    {describeExpiry(c.daysUntilExpiry)}
                  </div>
                  <div className="tiny muted">
                    {c.expiresOn ? `Gyldigt til ${formatDate(c.expiresOn)}` : 'Ingen udløbsdato'}
                    {c.issuedOn && ` · udstedt ${formatDate(c.issuedOn)}`}
                  </div>
                </div>

                {isAdmin && (
                  <div className="row" style={{ gap: 6 }}>
                    <button className="btn btn--sm" onClick={() => setEditing(c)}>
                      Ret
                    </button>
                    <button className="btn btn--danger btn--sm" onClick={() => void handleDelete(c)}>
                      Slet
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {editing !== undefined && (
        <CertificateDialog certificate={editing} onClose={() => setEditing(undefined)} />
      )}
    </>
  )
}

function CertificateDialog({
  certificate,
  onClose
}: {
  certificate: Certificate | null
  onClose: () => void
}) {
  const { data: users } = useUsers()
  const { data: suggestions } = useCertificateSuggestions()
  const save = useSaveCertificate()

  const [form, setForm] = useState<CertificateInput>({
    userId: certificate?.user.id ?? 0,
    name: certificate?.name ?? '',
    issuer: certificate?.issuer ?? '',
    certificateNumber: certificate?.certificateNumber ?? '',
    issuedOn: certificate?.issuedOn ?? '',
    expiresOn: certificate?.expiresOn ?? '',
    notes: certificate?.notes ?? '',
    isActive: certificate?.isActive ?? true
  })
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await save.mutateAsync({
        id: certificate?.id,
        body: {
          ...form,
          issuer: form.issuer?.trim() || null,
          certificateNumber: form.certificateNumber?.trim() || null,
          issuedOn: form.issuedOn || null,
          expiresOn: form.expiresOn || null,
          notes: form.notes?.trim() || null
        }
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke gemme beviset.')
    }
  }

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="modal__card">
        <h2 style={{ margin: '0 0 14px', fontSize: 18 }}>
          {certificate ? 'Ret bevis' : 'Registrér bevis'}
        </h2>

        <form onSubmit={(e) => void submit(e)}>
          <label className="field">
            <span className="field__label">Hvem</span>
            <select
              value={form.userId}
              required
              onChange={(e) => setForm({ ...form, userId: Number(e.target.value) })}
            >
              <option value={0} disabled>
                Vælg medarbejder ...
              </option>
              {(users ?? []).filter((u) => u.isActive).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.displayName}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Hvilket bevis</span>
            {/* Fri tekst med forslag: hvert hus har sine egne, men de samme
                skal helst hedde det samme, saa man kan se hvem der mangler hvad. */}
            <input
              list="certificate-suggestions"
              value={form.name}
              required
              placeholder="F.eks. L-AUS"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <datalist id="certificate-suggestions">
              {(suggestions ?? []).map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </label>

          <div className="row" style={{ gap: 10 }}>
            <label className="field" style={{ flex: 1 }}>
              <span className="field__label">Udstedt</span>
              <input
                type="date"
                value={form.issuedOn ?? ''}
                onChange={(e) => setForm({ ...form, issuedOn: e.target.value })}
              />
            </label>
            <label className="field" style={{ flex: 1 }}>
              <span className="field__label">Gyldigt til</span>
              <input
                type="date"
                value={form.expiresOn ?? ''}
                onChange={(e) => setForm({ ...form, expiresOn: e.target.value })}
              />
            </label>
          </div>

          <p className="small muted" style={{ marginTop: 0 }}>
            {form.expiresOn
              ? 'Systemet varsler selv i god tid, før beviset udløber.'
              : 'Uden en udløbsdato varsles der aldrig - brug det til beviser, der ikke udløber.'}
          </p>

          <div className="row" style={{ gap: 10 }}>
            <label className="field" style={{ flex: 1 }}>
              <span className="field__label">Udsteder</span>
              <input
                value={form.issuer ?? ''}
                placeholder="F.eks. TEKNIQ"
                onChange={(e) => setForm({ ...form, issuer: e.target.value })}
              />
            </label>
            <label className="field" style={{ flex: 1 }}>
              <span className="field__label">Bevisnummer</span>
              <input
                value={form.certificateNumber ?? ''}
                onChange={(e) => setForm({ ...form, certificateNumber: e.target.value })}
              />
            </label>
          </div>

          <label className="field">
            <span className="field__label">Note</span>
            <input
              value={form.notes ?? ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </label>

          <label className="cal-person" style={{ cursor: 'pointer', marginBottom: 10 }}>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            Gyldigt bevis
          </label>
          <p className="tiny muted" style={{ marginTop: 0 }}>
            Fjern fluebenet, når beviset er erstattet af et nyt. Så bliver det stående i
            historikken, men rykker ikke længere.
          </p>

          {error && <ErrorBox error={error} />}

          <div className="row" style={{ gap: 8, marginTop: 12 }}>
            <button className="btn btn--primary" type="submit" disabled={save.isPending || !form.userId}>
              {save.isPending ? 'Gemmer ...' : 'Gem'}
            </button>
            <button className="btn" type="button" onClick={onClose}>
              Annullér
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
